import { Radio, ShowMoreLess } from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode, useState, useEffect } from 'react'
import {
  EnrichedBankAccountDetails,
  EnrichedCardDetails,
  isSavedBankAccountValid,
  isSavedCard,
  isSavedCardValid,
  PaymentMethodType,
  SelectedPaymentMethod,
  Features,
} from '../../model'
import {
  useApplePayFeature,
  useApplePayPayment,
  useCashAppPay,
  useGetShowMoreLessHandler,
  useSavedBankAccounts,
  useSavedCards,
  useSelectedPaymentType,
  useShouldShowApplePayBanner,
  useCheckoutState,
  useIsAfterpayFinanceFeeEligible,
  useAppDispatch,
  setSavedCards,
  useIsApplePaylater,
  useIsApplePaylaterWithApplePayRepayment,
} from '../../state'
import { PaymentType, CardBrand } from '@afterpay/types'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { useApplePayTooltip } from '../PaymentMethodApplePay/useApplePayTooltip'
import { CardPayment } from '../InlinePaymentMethod/CardPayment'
import { BankAccountPayment } from '../InlinePaymentMethod/BankAccountPayment'
import { CashAppPayment } from '../InlinePaymentMethod/CashAppPayment'
import { ApplePayPayment } from '../InlinePaymentMethod/ApplePayPayment'
import { PaymentMethodErrorMessage } from './PaymentMethodErrorMessage'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { useFlag } from '@afterpaytouch/feature'
import { PaymentCreditCardType } from '@afterpaytouch/portal-api'
import { TOPAZ_ERROR_REASONS } from '../../model/topaz'

interface PaymentMethodListProps {
  onChange: (method: SelectedPaymentMethod) => void
  current?: SelectedPaymentMethod
  labelledBy?: string
}

const getPaymentMethodIdentifier = (
  current: SelectedPaymentMethod | undefined,
  savedCards: EnrichedCardDetails[],
  savedBankAccounts: EnrichedBankAccountDetails[] | undefined
): string | undefined => {
  switch (current?.type) {
    case PaymentMethodType.APPLE_PAY:
      return PaymentMethodType.APPLE_PAY
    case PaymentMethodType.CASH_APP_PAY:
      return 'cashAppPay' in current && current.cashAppPay.id ? current.cashAppPay.id.toString() : undefined
    case PaymentMethodType.ACH_BANK_ACCOUNT:
      return 'account' in current && isSavedBankAccountValid(current.account, savedBankAccounts) ? current.account.id.toString() : undefined
    case PaymentMethodType.CREDIT_CARD:
      if ('card' in current && isSavedCard(current.card) && isSavedCardValid(current.card, savedCards)) {
        return current.card.token
      }
  }
}

export const PaymentMethodListRedesigned: FunctionComponent<PaymentMethodListProps> = ({ onChange, current, labelledBy = '' }) => {
  const [isPaymentMethodListShowingMore, setIsPaymentMethodListShowingMore] = useState(false)
  const dispatch = useAppDispatch()
  const applePayEnabled = useApplePayPayment()
  const applePayFeature = useApplePayFeature()
  const savedCards = useSavedCards()
  const savedBankAccounts = useSavedBankAccounts()
  const sortedSavedBankAccounts =
    savedBankAccounts != null && savedBankAccounts !== undefined
      ? [...savedBankAccounts].sort((a, b) => {
          // put disallowed ones at the bottom
          return (a.fraudCheckAchDisallowed ? 1 : 0) - (b.fraudCheckAchDisallowed ? 1 : 0)
        })
      : []
  const cashAppPay = useCashAppPay()
  const paymentMethodIdentifier = getPaymentMethodIdentifier(current, savedCards, savedBankAccounts)
  const [shouldShowApplePayBanner, saveApplePayBannerTag] = useShouldShowApplePayBanner()
  const selectedPaymentType = useSelectedPaymentType() ?? PaymentType.PBI
  const handleShowMoreLess = useGetShowMoreLessHandler('Inline Payment Method')
  const { tooltipTriggerRef, openTooltip } = useApplePayTooltip(-10)
  const { error } = useCheckoutState()
  const isAff = useIsAfterpayFinanceFeeEligible()
  const cashPayMonthlyEnabled = useFlag(Features.CONVERGENCE_CASH_PAY_MONTHLY_ENABLED)
  const blockCapOneDebitCards = useFlag(Features.CHECKOUT_BLOCK_CAP_ONE_MC_DEBIT_CARDS, false)
  const isApplePaylater = useIsApplePaylater()
  const isApplePayRepaymentEnabled = useIsApplePaylaterWithApplePayRepayment()

  const id = 'apple-pay-learn-more-tooltip'

  const currentPaymentMethodType = valueIsNotNullOrUndefined(paymentMethodIdentifier) ? current?.type : undefined

  useEffect(() => {
    if (!blockCapOneDebitCards || savedCards.length === 0) {
      return
    }

    const selectedCardToken = current?.type === PaymentMethodType.CREDIT_CARD && 'card' in current && 'token' in current.card ? current.card.token : null

    const updatedCards = savedCards.map((card) => {
      if (card.token === selectedCardToken || card.allowed === false) {
        return card
      }

      const isCapitalOne = card.issuingBank?.toUpperCase().includes('CAPITAL ONE')
      const isMastercard = card.cardBrand === CardBrand.MASTERCARD
      const isDebit = card.creditCardType === PaymentCreditCardType.DEBIT

      if (isCapitalOne && isMastercard && isDebit) {
        return { ...card, error: TOPAZ_ERROR_REASONS.UnacceptableIssuerRestrictedCard }
      }

      return card
    })

    const hasChanges = updatedCards.some((c, idx) => c.error !== savedCards[idx].error)
    if (hasChanges) {
      dispatch(setSavedCards(updatedCards))
    }
  }, [blockCapOneDebitCards, savedCards, current, dispatch])

  const renderSelectedPaymentMethod = (): ReactNode => {
    switch (currentPaymentMethodType) {
      case PaymentMethodType.APPLE_PAY:
        return renderApplePay()
      case PaymentMethodType.CASH_APP_PAY:
        return renderCashAppPay()
      case PaymentMethodType.ACH_BANK_ACCOUNT:
        return renderSavedBankAccounts(sortedSavedBankAccounts.filter((account) => account.id.toString() === paymentMethodIdentifier))
      case PaymentMethodType.CREDIT_CARD:
        return renderSavedCards(savedCards.filter((card) => card.token === paymentMethodIdentifier))
      default:
        return null
    }
  }

  const renderSavedCards = (cards: EnrichedCardDetails[]): ReactNode => {
    return cards.map((card) => {
      const selectedCardHasError = card.token === paymentMethodIdentifier && valueIsNotNullOrUndefined(error)
      const isDisabled = valueIsNotNullOrUndefined(card.error) || selectedCardHasError || (isAff && card.creditCardType !== 'DEBIT')
      return [
        <>
          <Radio.Button
            disabled={isDisabled}
            key={`payment-method-option-${card.token}`}
            label={() => (
              <>
                <CardPayment iconSize='Large' card={card} disabled={isDisabled} />
                <PaymentMethodErrorMessage paymentMethodType={PaymentMethodType.CREDIT_CARD} card={card} />
              </>
            )}
            labelPlacement='Top'
            labelWidth='Full'
            onClick={(e) => handlePaymentMethodSelection(e, PaymentMethodType.CREDIT_CARD)}
            value={card.token}
            testNameSpace={`payment-method-option-${card.token}`}
            roundedBorder
          />
        </>,
      ]
    })
  }

  const renderCashAppPay = (): ReactNode => {
    return cashAppPay.map((cashAppPayItem) => {
      const isConsumerLending = selectedPaymentType === PaymentType.PCL
      const isInactive = cashAppPayItem.status !== 'ACTIVE'
      const isDisabled = (isConsumerLending && !cashPayMonthlyEnabled) || isInactive || isAff

      return [
        <>
          <Radio.Button
            key={`payment-method-option-${cashAppPayItem.id}`}
            disabled={isDisabled}
            label={() => (
              <>
                <CashAppPayment iconSize='Large' cashtag={cashAppPayItem.cashtag} validatePaymentType status={cashAppPayItem.status} disabled={isDisabled} />
                <PaymentMethodErrorMessage paymentMethodType={PaymentMethodType.CASH_APP_PAY} cashAppPay={cashAppPayItem} />
              </>
            )}
            labelPlacement='Top'
            labelWidth='Full'
            onClick={(e) => handlePaymentMethodSelection(e, PaymentMethodType.CASH_APP_PAY)}
            value={cashAppPayItem.id.toString()}
            roundedBorder
          />
        </>,
      ]
    })
  }

  const renderSavedBankAccounts = (bankAccounts: EnrichedBankAccountDetails[]): ReactNode => {
    return bankAccounts.map((account, index) => {
      const isDisabled = isAff || account.error !== null || Boolean(account.fraudCheckAchDisallowed) || isApplePaylater
      return [
        <>
          <Radio.Button
            disabled={isDisabled}
            key={`payment-method-option-${index}`}
            label={() => (
              <>
                <BankAccountPayment iconSize='Large' account={account} disabled={isDisabled} />
                <PaymentMethodErrorMessage paymentMethodType={PaymentMethodType.ACH_BANK_ACCOUNT} account={account} />
              </>
            )}
            labelPlacement='Top'
            labelWidth='Full'
            onClick={(e) => handlePaymentMethodSelection(e, PaymentMethodType.ACH_BANK_ACCOUNT)}
            value={account.id.toString()}
            roundedBorder
          />
        </>,
      ]
    })
  }

  const renderApplePay = (): ReactNode => {
    if (!applePayEnabled || (isApplePaylater && !isApplePayRepaymentEnabled)) {
      return null
    }
    const isDisabled = !applePayFeature.enabledForConsumerLending || isAff
    return [
      <Radio.Button
        itemRef={tooltipTriggerRef}
        labelWidth='Full'
        label={() => (
          <ApplePayPayment
            showLearnMore={shouldShowApplePayBanner}
            handleToggleTooltip={openTooltip}
            validatePaymentType
            iconSize='Large'
            ariaDescribedBy={id}
            disabled={isDisabled}
            showPaymentMethodInfo={isApplePayRepaymentEnabled}
          />
        )}
        value='APPLE_PAY'
        testNameSpace='payment-method-applepay-radio'
        key='payment-method-option-apple-pay'
        labelPlacement='Top'
        disabled={isDisabled}
        onClick={(e) => {
          handlePaymentMethodSelection(e, PaymentMethodType.APPLE_PAY)
          if (shouldShowApplePayBanner) {
            saveApplePayBannerTag()
          }
        }}
        roundedBorder
      />,
    ]
  }

  const handlePaymentMethodSelection = (e, type: PaymentMethodType): void => {
    let payload
    if (type === PaymentMethodType.APPLE_PAY) {
      payload = { type }
    } else if (type === PaymentMethodType.CASH_APP_PAY) {
      payload = { type, cashAppPay: cashAppPay.find((saved) => saved.id === Number(e.target.value)) }
    } else if (type === PaymentMethodType.ACH_BANK_ACCOUNT) {
      payload = { account: savedBankAccounts?.find((saved) => saved.id === Number(e.target.value)), type }
    } else {
      payload = { card: { token: e.target.value }, type }
    }
    onChange(payload)
    if (isPaymentMethodListShowingMore) {
      setIsPaymentMethodListShowingMore(false)
    }
  }

  const onShowMoreLessChange = (isShowingMore: boolean): void => {
    setIsPaymentMethodListShowingMore(isShowingMore)
  }

  const onShowMoreLessClick = (e: React.SyntheticEvent): void => {
    handleShowMoreLess(!isPaymentMethodListShowingMore)
  }

  const unselectedCards = savedCards.filter((card) => card.token !== paymentMethodIdentifier)
  const unselectedBankAccounts = sortedSavedBankAccounts.filter((account) => account.id.toString() !== paymentMethodIdentifier)

  return (
    <MaskedElement actionName='Select Payment Method'>
      <Radio.Group name='card' indent kind='Option' value={paymentMethodIdentifier} aria-labelledby={labelledBy} reversed>
        <ShowMoreLess onChange={onShowMoreLessChange} onClick={onShowMoreLessClick} isShowingMore={isPaymentMethodListShowingMore}>
          {renderSelectedPaymentMethod()}
          {currentPaymentMethodType !== PaymentMethodType.CASH_APP_PAY && renderCashAppPay()}
          {renderSavedCards(unselectedCards)}
          {renderSavedBankAccounts(unselectedBankAccounts)}
          {currentPaymentMethodType !== PaymentMethodType.APPLE_PAY && renderApplePay()}
        </ShowMoreLess>
      </Radio.Group>
    </MaskedElement>
  )
}
